import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import './index.css';

import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { WishlistProvider } from './context/WishlistContext';

import Navbar from './components/Navbar';
import Footer from './components/Footer';

const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderSuccess = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.OrderSuccess })));
const CustomOrder = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.CustomOrder })));
const BuildABouquet = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.BuildABouquet })));
const About = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.About })));
const Contact = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.Contact })));
const Wishlist = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.Wishlist })));
const TrackOrder = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.TrackOrder })));
const FAQ = lazy(() => import('./pages/PublicPages').then(m => ({ default: m.FAQ })));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminPages').then(m => ({ default: m.AdminOrders })));
const AdminCategories = lazy(() => import('./pages/admin/AdminPages').then(m => ({ default: m.AdminCategories })));
const AdminCustomOrders = lazy(() => import('./pages/admin/AdminPages').then(m => ({ default: m.AdminCustomOrders })));
const AdminReviews = lazy(() => import('./pages/admin/AdminPages').then(m => ({ default: m.AdminReviews })));
const AdminHomepage = lazy(() => import('./pages/admin/AdminHomepage'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminOrderPrint = lazy(() => import('./pages/admin/AdminOrderPrint'));

function PageLoader() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
      Loading...
    </div>
  );
}

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

function AnimatedPage({ children }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
}

function PublicSite() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-primary)', transition: 'background 0.3s ease' }}>
      <Navbar />
      <main style={{ flex: 1 }}>
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<AnimatedPage><Home /></AnimatedPage>} />
              <Route path="/shop" element={<AnimatedPage><Shop /></AnimatedPage>} />
              <Route path="/shop/:category" element={<AnimatedPage><Shop /></AnimatedPage>} />
              <Route path="/product/:id" element={<AnimatedPage><ProductDetail /></AnimatedPage>} />
              <Route path="/cart" element={<AnimatedPage><Cart /></AnimatedPage>} />
              <Route path="/checkout" element={<AnimatedPage><Checkout /></AnimatedPage>} />
              <Route path="/order-success/:orderId" element={<AnimatedPage><OrderSuccess /></AnimatedPage>} />
              <Route path="/custom-order" element={<AnimatedPage><CustomOrder /></AnimatedPage>} />
              <Route path="/build-a-bouquet" element={<AnimatedPage><BuildABouquet /></AnimatedPage>} />
              <Route path="/about" element={<AnimatedPage><About /></AnimatedPage>} />
              <Route path="/contact" element={<AnimatedPage><Contact /></AnimatedPage>} />
              <Route path="/wishlist" element={<AnimatedPage><Wishlist /></AnimatedPage>} />
              <Route path="/track-order" element={<AnimatedPage><TrackOrder /></AnimatedPage>} />
              <Route path="/faq" element={<AnimatedPage><FAQ /></AnimatedPage>} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <CartProvider>
        <WishlistProvider>
          <Router>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/orders/:id/print" element={<AdminOrderPrint />} />
                <Route path="/admin/custom-requests" element={<AdminCustomOrders />} />
                <Route path="/admin/reviews" element={<AdminReviews />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/homepage" element={<AdminHomepage />} />
                <Route path="/admin/reports" element={<AdminReports />} />
                <Route path="/*" element={<PublicSite />} />
              </Routes>
            </Suspense>
          </Router>
        </WishlistProvider>
        </CartProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
